import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { motion } from "framer-motion";
import {
  Buildings, Users, ChartLineUp, Heartbeat, BookOpen, Timer,
  Plus, Trash, UserPlus, CaretRight, Crown, TrendUp, ChatCircleDots,
  EnvelopeSimple, Copy, Check, Clock, Trophy, Target, Lightning,
} from "@phosphor-icons/react";
import { Textarea } from "../components/ui/textarea";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_COLORS = {
  happy: "#F4A261", calm: "#84A98C", energized: "#E9C46A", stressed: "#E76F51",
  anxious: "#CDB4DB", sad: "#457B9D", tired: "#A8DADC", frustrated: "#E07A5F",
  grateful: "#2D6A4F", hopeful: "#FFD166",
};

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgAnalytics, setOrgAnalytics] = useState(null);
  const [view, setView] = useState("overview"); // overview | orgs | org-detail
  const [newOrgName, setNewOrgName] = useState("");
  const [newEmployeeEmail, setNewEmployeeEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [copiedToken, setCopiedToken] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [chalTitle, setChalTitle] = useState("");
  const [chalDesc, setChalDesc] = useState("");
  const [chalType, setChalType] = useState("daily_checkin");
  const [chalGoal, setChalGoal] = useState(7);
  const [chalDays, setChalDays] = useState(7);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);

  useEffect(() => {
    loadOverview();
    loadOrgs();
  }, []);

  const loadOverview = async () => {
    try {
      const res = await axios.get(`${API}/admin/overview`, { withCredentials: true });
      setOverview(res.data);
    } catch (err) {
      console.error("Admin overview failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadOrgs = async () => {
    try {
      const res = await axios.get(`${API}/admin/organizations`, { withCredentials: true });
      setOrgs(res.data);
    } catch {}
  };

  const createOrg = async () => {
    if (!newOrgName.trim()) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/organizations`, { name: newOrgName.trim() }, { withCredentials: true });
      setNewOrgName("");
      await loadOrgs();
    } catch (err) {
      console.error("Create org failed:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const selectOrg = async (org) => {
    setSelectedOrg(org);
    setView("org-detail");
    try {
      const [detailRes, analyticsRes, inviteRes, chalRes] = await Promise.all([
        axios.get(`${API}/admin/organizations/${org.id}`, { withCredentials: true }),
        axios.get(`${API}/admin/organizations/${org.id}/analytics`, { withCredentials: true }),
        axios.get(`${API}/admin/invitations/${org.id}`, { withCredentials: true }),
        axios.get(`${API}/admin/challenges/${org.id}`, { withCredentials: true }),
      ]);
      setSelectedOrg(detailRes.data);
      setOrgAnalytics(analyticsRes.data);
      setInvitations(inviteRes.data);
      setChallenges(chalRes.data);
      setSelectedChallenge(null);
      setLeaderboard(null);
    } catch (err) {
      console.error("Org detail failed:", err);
    }
  };

  const sendInvitation = async () => {
    if (!inviteEmail.trim() || !selectedOrg) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/invitations`, {
        email: inviteEmail.trim(),
        org_id: selectedOrg.id,
        message: inviteMessage.trim() || null,
      }, { withCredentials: true });
      setInviteEmail("");
      setInviteMessage("");
      const res = await axios.get(`${API}/admin/invitations/${selectedOrg.id}`, { withCredentials: true });
      setInvitations(res.data);
    } catch (err) {
      console.error("Invite failed:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const revokeInvitation = async (inviteId) => {
    try {
      await axios.delete(`${API}/admin/invitations/${inviteId}`, { withCredentials: true });
      setInvitations((prev) => prev.map((inv) => inv.id === inviteId ? { ...inv, status: "revoked" } : inv));
    } catch {}
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const loadChallenges = async (orgId) => {
    try {
      const res = await axios.get(`${API}/admin/challenges/${orgId}`, { withCredentials: true });
      setChallenges(res.data);
    } catch {}
  };

  const createChallenge = async () => {
    if (!chalTitle.trim() || !selectedOrg) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/challenges`, {
        org_id: selectedOrg.id,
        title: chalTitle.trim(),
        description: chalDesc.trim(),
        challenge_type: chalType,
        goal: chalGoal,
        duration_days: chalDays,
      }, { withCredentials: true });
      setChalTitle(""); setChalDesc(""); setChalGoal(7); setChalDays(7);
      await loadChallenges(selectedOrg.id);
    } catch (err) {
      console.error("Create challenge failed:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const viewLeaderboard = async (challenge) => {
    setSelectedChallenge(challenge);
    try {
      const res = await axios.get(
        `${API}/admin/challenges/${challenge.org_id}/${challenge.id}/leaderboard`,
        { withCredentials: true }
      );
      setLeaderboard(res.data);
    } catch (err) {
      console.error("Leaderboard failed:", err);
    }
  };

  const endChallenge = async (chalId) => {
    try {
      await axios.delete(`${API}/admin/challenges/${chalId}`, { withCredentials: true });
      if (selectedOrg) await loadChallenges(selectedOrg.id);
      setSelectedChallenge(null);
      setLeaderboard(null);
    } catch {}
  };

  const addEmployee = async () => {
    if (!newEmployeeEmail.trim() || !selectedOrg) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/organizations/${selectedOrg.id}/employees`, { email: newEmployeeEmail.trim() }, { withCredentials: true });
      setNewEmployeeEmail("");
      await selectOrg(selectedOrg);
    } catch (err) {
      console.error("Add employee failed:", err);
    } finally {
      setActionLoading(false);
    }
  };

  const removeEmployee = async (email) => {
    if (!selectedOrg) return;
    try {
      await axios.delete(`${API}/admin/organizations/${selectedOrg.id}/employees/${email}`, { withCredentials: true });
      await selectOrg(selectedOrg);
    } catch {}
  };

  const moodPieData = orgAnalytics?.mood_distribution
    ? Object.entries(orgAnalytics.mood_distribution).map(([mood, count]) => ({
        name: mood.charAt(0).toUpperCase() + mood.slice(1),
        value: count,
        color: MOOD_COLORS[mood] || "#78857F",
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 rounded-full bg-[#2D6A4F] animate-breathe" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2D6A4F] mb-2">B2B Admin</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-light text-[#1A2421] tracking-tight">
            Wellness Program Manager
          </h1>
        </div>
        <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] text-xs font-bold">
          <Buildings size={14} weight="duotone" /> Admin
        </span>
      </div>

      {/* Nav Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: "overview", label: "Overview", icon: ChartLineUp },
          { key: "orgs", label: "Organizations", icon: Buildings },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => { setView(t.key); if (t.key === "overview") loadOverview(); }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              view === t.key || (view === "org-detail" && t.key === "orgs")
                ? "bg-[#2D6A4F] text-white shadow-md"
                : "bg-white text-[#78857F] hover:bg-[#2D6A4F]/5 border border-[#E1D9CD]"
            }`}
          >
            <t.icon size={16} weight="duotone" />
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {view === "overview" && overview && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total Users", value: overview.total_users, icon: Users, color: "#2D6A4F" },
              { label: "Premium Users", value: overview.premium_users, icon: Crown, color: "#F4A261" },
              { label: "Organizations", value: overview.total_organizations, icon: Buildings, color: "#84A98C" },
              { label: "Active This Week", value: overview.active_users_this_week, icon: TrendUp, color: "#CDB4DB" },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="card-neuro p-5"
                data-testid={`admin-stat-${i}`}
              >
                <stat.icon size={22} weight="duotone" style={{ color: stat.color }} className="mb-2" />
                <p className="font-['Outfit'] text-2xl font-medium text-[#1A2421]">{stat.value}</p>
                <p className="text-xs text-[#78857F]">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total Mood Entries", value: overview.total_mood_entries, icon: Heartbeat },
              { label: "Total Sessions", value: overview.total_sessions, icon: Timer },
              { label: "Journal Entries", value: overview.total_journals, icon: BookOpen },
              { label: "Community Posts", value: overview.total_community_posts, icon: ChatCircleDots },
            ].map((stat, i) => (
              <div key={stat.label} className="card-neuro p-4 flex items-center gap-3">
                <stat.icon size={20} weight="duotone" className="text-[#84A98C] shrink-0" />
                <div>
                  <p className="font-['Outfit'] text-lg font-medium text-[#1A2421]">{stat.value}</p>
                  <p className="text-[10px] text-[#78857F]">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Organizations List ── */}
      {view === "orgs" && (
        <div>
          {/* Create Org */}
          <div className="card-neuro p-6 mb-6" data-testid="create-org-form">
            <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] mb-3">Create Organization</h3>
            <div className="flex gap-3">
              <Input
                data-testid="new-org-name-input"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="Company name"
                className="flex-1 rounded-xl border-[#E1D9CD] bg-[#FAF7F2]"
              />
              <Button
                onClick={createOrg}
                disabled={actionLoading || !newOrgName.trim()}
                data-testid="create-org-button"
                className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4936] text-white px-6"
              >
                <Plus size={16} weight="bold" className="mr-1" />
                Create
              </Button>
            </div>
          </div>

          {/* Org List */}
          <div className="space-y-3" data-testid="org-list">
            {orgs.map((org) => (
              <button
                key={org.id}
                data-testid={`org-card-${org.id}`}
                onClick={() => selectOrg(org)}
                className="card-neuro p-5 w-full text-left flex items-center gap-4 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#2D6A4F]/10 flex items-center justify-center shrink-0">
                  <Buildings size={24} weight="duotone" className="text-[#2D6A4F]" />
                </div>
                <div className="flex-1">
                  <p className="font-['Outfit'] text-lg font-medium text-[#1A2421]">{org.name}</p>
                  <p className="text-xs text-[#78857F]">{org.employee_count || 0} employees</p>
                </div>
                <CaretRight size={20} className="text-[#78857F]" />
              </button>
            ))}
            {orgs.length === 0 && (
              <p className="text-center text-sm text-[#78857F] py-8">No organizations yet. Create one above.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Org Detail ── */}
      {view === "org-detail" && selectedOrg && (
        <div>
          <button
            onClick={() => setView("orgs")}
            className="text-sm text-[#2D6A4F] hover:underline mb-4 flex items-center gap-1"
            data-testid="back-to-orgs"
          >
            &larr; Back to Organizations
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-[#2D6A4F]/10 flex items-center justify-center">
              <Buildings size={28} weight="duotone" className="text-[#2D6A4F]" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-2xl font-medium text-[#1A2421]">{selectedOrg.name}</h2>
              <p className="text-sm text-[#78857F]">{selectedOrg.employee_count || 0} employees</p>
            </div>
          </div>

          {/* Org Analytics */}
          {orgAnalytics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Employees", value: orgAnalytics.total_employees },
                { label: "Active (7d)", value: orgAnalytics.active_users },
                { label: "Mood Entries", value: orgAnalytics.total_mood_entries },
                { label: "Engagement", value: `${orgAnalytics.engagement_rate}%` },
              ].map((s, i) => (
                <div key={i} className="card-neuro p-4 text-center" data-testid={`org-stat-${i}`}>
                  <p className="font-['Outfit'] text-xl font-medium text-[#2D6A4F]">{s.value}</p>
                  <p className="text-[10px] text-[#78857F]">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Org Mood Distribution */}
          {moodPieData.length > 0 && (
            <div className="card-neuro p-6 mb-6" data-testid="org-mood-chart">
              <h3 className="text-sm font-semibold text-[#1A2421] mb-3">Organization Mood Distribution</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={moodPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {moodPieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {moodPieData.map((d) => (
                  <div key={d.name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-[10px] text-[#78857F]">{d.name} ({d.value})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Employee */}
          <div className="card-neuro p-6 mb-6" data-testid="add-employee-form">
            <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] mb-3">Add Employee</h3>
            <div className="flex gap-3">
              <Input
                data-testid="new-employee-email-input"
                value={newEmployeeEmail}
                onChange={(e) => setNewEmployeeEmail(e.target.value)}
                placeholder="employee@company.com"
                type="email"
                className="flex-1 rounded-xl border-[#E1D9CD] bg-[#FAF7F2]"
              />
              <Button
                onClick={addEmployee}
                disabled={actionLoading || !newEmployeeEmail.trim()}
                data-testid="add-employee-button"
                className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4936] text-white px-6"
              >
                <UserPlus size={16} weight="bold" className="mr-1" />
                Add
              </Button>
            </div>
          </div>

          {/* Employee List */}
          <div data-testid="employee-list">
            <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] mb-3">Employees</h3>
            <div className="space-y-2">
              {(selectedOrg.employees || []).map((emp) => (
                <div key={emp.user_id} className="card-neuro p-4 flex items-center gap-3" data-testid={`employee-${emp.user_id}`}>
                  <div className="w-9 h-9 rounded-full bg-[#84A98C]/15 flex items-center justify-center text-xs font-bold text-[#2D6A4F]">
                    {emp.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A2421]">{emp.name}</p>
                    <p className="text-xs text-[#78857F] truncate">{emp.email}</p>
                  </div>
                  {emp.is_premium && (
                    <Crown size={14} weight="fill" className="text-[#F4A261]" />
                  )}
                  <button
                    onClick={() => removeEmployee(emp.email)}
                    className="p-1.5 rounded-lg text-[#78857F] hover:text-[#E76F51] hover:bg-[#E76F51]/10 transition-all"
                    data-testid={`remove-employee-${emp.user_id}`}
                  >
                    <Trash size={16} weight="duotone" />
                  </button>
                </div>
              ))}
              {(!selectedOrg.employees || selectedOrg.employees.length === 0) && (
                <p className="text-center text-sm text-[#78857F] py-6">No employees yet. Send an invitation below.</p>
              )}
            </div>
          </div>

          {/* ── Email Invitations ── */}
          <div className="mt-6" data-testid="invitation-section">
            <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] mb-3">
              <EnvelopeSimple size={20} weight="duotone" className="inline mr-2 text-[#84A98C]" />
              Email Invitations
            </h3>

            <div className="card-neuro p-6 mb-4">
              <div className="space-y-3">
                <Input
                  data-testid="invite-email-input"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="employee@company.com"
                  type="email"
                  className="rounded-xl border-[#E1D9CD] bg-[#FAF7F2]"
                />
                <Input
                  data-testid="invite-message-input"
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  placeholder="Personal message (optional)"
                  className="rounded-xl border-[#E1D9CD] bg-[#FAF7F2]"
                />
                <Button
                  onClick={sendInvitation}
                  disabled={actionLoading || !inviteEmail.trim()}
                  data-testid="send-invite-button"
                  className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4936] text-white px-6"
                >
                  <EnvelopeSimple size={16} weight="bold" className="mr-1.5" />
                  Send Invitation
                </Button>
              </div>
            </div>

            {/* Pending Invitations */}
            {invitations.length > 0 && (
              <div className="space-y-2" data-testid="invitation-list">
                {invitations.map((inv) => {
                  const isPending = inv.status === "pending";
                  return (
                    <div key={inv.id} className="card-neuro p-4 flex items-center gap-3" data-testid={`invitation-${inv.id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        isPending ? "bg-[#F4A261]/15" : inv.status === "accepted" ? "bg-[#84A98C]/15" : "bg-[#78857F]/10"
                      }`}>
                        {isPending ? <Clock size={16} className="text-[#F4A261]" /> :
                         inv.status === "accepted" ? <Check size={16} className="text-[#84A98C]" /> :
                         <Trash size={16} className="text-[#78857F]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A2421]">{inv.email}</p>
                        <p className="text-[10px] text-[#78857F]">
                          {inv.status === "pending" ? "Pending" : inv.status === "accepted" ? "Accepted" : "Revoked"}
                          {" · "}{new Date(inv.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </p>
                      </div>
                      {isPending && (
                        <>
                          <button
                            onClick={() => copyInviteLink(inv.token)}
                            data-testid={`copy-invite-${inv.id}`}
                            className="p-1.5 rounded-lg text-[#2D6A4F] hover:bg-[#2D6A4F]/10 transition-all"
                            title="Copy invite link"
                          >
                            {copiedToken === inv.token ? <Check size={16} /> : <Copy size={16} />}
                          </button>
                          <button
                            onClick={() => revokeInvitation(inv.id)}
                            className="p-1.5 rounded-lg text-[#78857F] hover:text-[#E76F51] hover:bg-[#E76F51]/10 transition-all"
                            data-testid={`revoke-invite-${inv.id}`}
                          >
                            <Trash size={16} weight="duotone" />
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Wellness Challenges ── */}
          <div className="mt-6" data-testid="challenges-section">
            <h3 className="font-['Outfit'] text-lg font-medium text-[#1A2421] mb-3">
              <Trophy size={20} weight="duotone" className="inline mr-2 text-[#F4A261]" />
              Wellness Challenges
            </h3>

            {/* Create Challenge */}
            {!selectedChallenge && (
              <div className="card-neuro p-6 mb-4" data-testid="create-challenge-form">
                <p className="text-sm font-medium text-[#1A2421] mb-3">Launch a New Challenge</p>
                <div className="space-y-3">
                  <Input
                    data-testid="challenge-title-input"
                    value={chalTitle}
                    onChange={(e) => setChalTitle(e.target.value)}
                    placeholder="Challenge title (e.g., 7-Day Mindfulness Sprint)"
                    className="rounded-xl border-[#E1D9CD] bg-[#FAF7F2]"
                  />
                  <Textarea
                    data-testid="challenge-desc-input"
                    value={chalDesc}
                    onChange={(e) => setChalDesc(e.target.value)}
                    placeholder="Brief description for your team..."
                    className="rounded-xl border-[#E1D9CD] bg-[#FAF7F2] resize-none min-h-[60px]"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-[10px] text-[#78857F] mb-1 block">Type</label>
                      <select
                        data-testid="challenge-type-select"
                        value={chalType}
                        onChange={(e) => setChalType(e.target.value)}
                        className="w-full rounded-xl border border-[#E1D9CD] bg-[#FAF7F2] px-3 py-2 text-sm text-[#1A2421]"
                      >
                        <option value="daily_checkin">Daily Check-ins</option>
                        <option value="sessions">Sensory Sessions</option>
                        <option value="journal">Journal Entries</option>
                        <option value="custom">Combined Activity</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-[#78857F] mb-1 block">Goal</label>
                      <Input data-testid="challenge-goal-input" type="number" value={chalGoal} onChange={(e) => setChalGoal(parseInt(e.target.value) || 1)} min={1} className="rounded-xl border-[#E1D9CD] bg-[#FAF7F2]" />
                    </div>
                    <div>
                      <label className="text-[10px] text-[#78857F] mb-1 block">Days</label>
                      <Input data-testid="challenge-days-input" type="number" value={chalDays} onChange={(e) => setChalDays(parseInt(e.target.value) || 1)} min={1} max={90} className="rounded-xl border-[#E1D9CD] bg-[#FAF7F2]" />
                    </div>
                  </div>
                  <Button onClick={createChallenge} disabled={actionLoading || !chalTitle.trim()} data-testid="create-challenge-button" className="rounded-full bg-[#F4A261] hover:bg-[#E8954F] text-white px-6">
                    <Trophy size={16} weight="bold" className="mr-1.5" />
                    Launch Challenge
                  </Button>
                </div>
              </div>
            )}

            {/* Challenge List */}
            {!selectedChallenge && challenges.length > 0 && (
              <div className="space-y-3" data-testid="challenge-list">
                {challenges.map((ch) => {
                  const isActive = ch.status === "active";
                  const typeLabels = { daily_checkin: "Check-ins", sessions: "Sessions", journal: "Journals", custom: "Combined" };
                  return (
                    <div key={ch.id} className="card-neuro p-4 flex items-center gap-3" data-testid={`challenge-card-${ch.id}`}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isActive ? "bg-[#F4A261]/15" : "bg-[#78857F]/10"}`}>
                        <Trophy size={20} weight="duotone" className={isActive ? "text-[#F4A261]" : "text-[#78857F]"} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A2421]">{ch.title}</p>
                        <p className="text-[10px] text-[#78857F]">{typeLabels[ch.challenge_type] || ch.challenge_type} · Goal: {ch.goal} · {ch.duration_days}d{isActive ? " · Active" : ` · ${ch.status}`}</p>
                      </div>
                      <button onClick={() => viewLeaderboard(ch)} data-testid={`view-leaderboard-${ch.id}`} className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#2D6A4F]/8 text-[#2D6A4F] hover:bg-[#2D6A4F]/15 transition-all">Leaderboard</button>
                      {isActive && <button onClick={() => endChallenge(ch.id)} className="p-1.5 rounded-lg text-[#78857F] hover:text-[#E76F51] hover:bg-[#E76F51]/10 transition-all" data-testid={`end-challenge-${ch.id}`}><Trash size={16} weight="duotone" /></button>}
                    </div>
                  );
                })}
              </div>
            )}
            {!selectedChallenge && challenges.length === 0 && (
              <p className="text-center text-sm text-[#78857F] py-4">No challenges yet. Create one above to engage your team.</p>
            )}

            {/* Leaderboard */}
            {selectedChallenge && leaderboard && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="leaderboard-view">
                <button onClick={() => { setSelectedChallenge(null); setLeaderboard(null); }} className="text-sm text-[#2D6A4F] hover:underline mb-4" data-testid="back-to-challenges">&larr; Back to Challenges</button>
                <div className="card-neuro p-6 mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Trophy size={24} weight="duotone" className="text-[#F4A261]" />
                    <div>
                      <h4 className="font-['Outfit'] text-lg font-medium text-[#1A2421]">{selectedChallenge.title}</h4>
                      <p className="text-xs text-[#78857F]">{selectedChallenge.description}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <div className="text-center p-3 rounded-xl bg-[#FAF7F2]"><p className="font-['Outfit'] text-xl font-medium text-[#2D6A4F]">{leaderboard.total_participants}</p><p className="text-[10px] text-[#78857F]">Participants</p></div>
                    <div className="text-center p-3 rounded-xl bg-[#FAF7F2]"><p className="font-['Outfit'] text-xl font-medium text-[#F4A261]">{leaderboard.completed_count}</p><p className="text-[10px] text-[#78857F]">Completed</p></div>
                    <div className="text-center p-3 rounded-xl bg-[#FAF7F2]"><p className="font-['Outfit'] text-xl font-medium text-[#84A98C]">{leaderboard.completion_rate}%</p><p className="text-[10px] text-[#78857F]">Rate</p></div>
                  </div>
                </div>
                <div className="space-y-2" data-testid="leaderboard-list">
                  {leaderboard.leaderboard.map((entry, i) => (
                    <div key={entry.user_id} className="card-neuro p-4 flex items-center gap-3" data-testid={`leaderboard-entry-${i}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? "bg-[#F4A261]/20 text-[#F4A261]" : i === 1 ? "bg-[#84A98C]/20 text-[#84A98C]" : i === 2 ? "bg-[#CDB4DB]/20 text-[#CDB4DB]" : "bg-[#EAE4D9] text-[#78857F]"}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1A2421]">{entry.name}</p>
                        <p className="text-[10px] text-[#78857F]">{entry.email}</p>
                      </div>
                      <div className="w-28 shrink-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-medium text-[#1A2421]">{entry.progress}/{entry.goal}</span>
                          {entry.completed && <Check size={12} weight="bold" className="text-[#2D6A4F]" />}
                        </div>
                        <div className="h-1.5 rounded-full bg-[#EAE4D9] overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, (entry.progress / entry.goal) * 100)}%`, backgroundColor: entry.completed ? "#2D6A4F" : "#F4A261" }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
